import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { confirmDelete, handleDeleteError } from '../utils/deleteHelper';

const EMPTY = {
  name:'', type:'external', contact_person:'', phone:'',
  email:'', address:'', payment_type:'cash', is_active:true
};
const PAYMENT_TYPES = ['cash','monthly_billing'];
const TYPE_LABELS = { external:'External', internal:'Internal' };
const PT_LABELS = { cash:'Cash', monthly_billing:'Monthly Billing' };

export default function CourierCompanies() {
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = ['admin','manager'].includes(user?.role);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get('/api/courier-companies');
      setCompanies(res.data);
    } catch (err) { console.error('courier-companies:', err); setCompanies([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user?.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/api/courier-companies/${editing}`, form);
      else await api.post('/api/courier-companies', form);
      setShowForm(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const handleToggle = async (co) => {
    try {
      await api.put(`/api/courier-companies/${co.id}`, { ...co, is_active: !co.is_active });
      load();
    } catch { alert('Update failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!confirmDelete('courier company', name)) return;
    try { await api.delete(`/api/courier-companies/${id}`); load(); }
    catch (err) { handleDeleteError(err); }
  };

  const editCompany = (co) => {
    setForm({
      name: co.name, type: co.type || 'external',
      contact_person: co.contact_person || '',
      phone: co.phone || '', email: co.email || '',
      address: co.address || '',
      payment_type: co.payment_type || 'cash',
      is_active: co.is_active
    });
    setEditing(co.id); setShowForm(true);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Courier Companies</h1>
          <p className="text-gray-500 text-sm">{companies.length} company(s) registered</p>
        </div>
        {isManager && (
          <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
            className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
            ➕ Add Courier Company
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map(co => (
            <div key={co.id} className={`bg-white rounded-xl border p-5 ${!co.is_active ? 'opacity-60' : 'hover:shadow-md'} transition-shadow`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-gray-800">{co.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    co.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>{co.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                  {TYPE_LABELS[co.type] || co.type}
                </span>
              </div>
              <div className="space-y-1 text-sm text-gray-500">
                {co.contact_person && <p>👤 {co.contact_person}</p>}
                {co.phone && <p>📞 {co.phone}</p>}
                {co.email && <p>✉️ {co.email}</p>}
                {co.address && <p>📍 {co.address}</p>}
                <p>💳 {PT_LABELS[co.payment_type] || co.payment_type}</p>
              </div>
              {isManager && (
                <div className="mt-4 flex gap-2 border-t pt-3">
                  <button onClick={() => editCompany(co)}
                    className="flex-1 text-xs py-1.5 border rounded hover:bg-gray-50">✏️ Edit</button>
                  <button onClick={() => handleToggle(co)}
                    className={`flex-1 text-xs py-1.5 border rounded ${
                      co.is_active ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-green-50 hover:text-green-600'
                    }`}>{co.is_active ? '🚫 Disable' : '✅ Enable'}</button>
                  {isAdmin && (
                    <button onClick={() => handleDelete(co.id, co.name)}
                      className="text-xs py-1.5 px-2 border rounded hover:bg-red-50 hover:text-red-600">🗑️</button>
                  )}
                </div>
              )}
            </div>
          ))}
          {companies.length === 0 && (
            <div className="col-span-3 bg-white rounded-xl border p-12 text-center text-gray-400">
              <p className="text-4xl mb-2">📦</p>
              <p>No courier companies added yet</p>
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{editing ? 'Edit Courier Company' : 'Add Courier Company'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <input value={form.name} onChange={e => setForm({...form,name:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.type} onChange={e => setForm({...form,type:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="external">External</option>
                  <option value="internal">Internal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                <select value={form.payment_type} onChange={e => setForm({...form,payment_type:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {PAYMENT_TYPES.map(p => <option key={p} value={p}>{PT_LABELS[p]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input value={form.contact_person} onChange={e => setForm({...form,contact_person:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input value={form.phone} onChange={e => setForm({...form,phone:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({...form,email:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {editing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={form.is_active ? 'active' : 'inactive'}
                    onChange={e => setForm({...form,is_active:e.target.value==='active'})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea value={form.address} onChange={e => setForm({...form,address:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
